import { getLayout, type ButtonDef } from './d200-layout';
import {
  buildBrightnessPacket,
  buildButtonZip,
  type ButtonEntry,
  CMD_IN_BUTTON,
  CMD_PARTIAL_UPDATE,
  CMD_SET_BUTTONS,
  MAGIC,
  parseButtonEvent,
  splitIntoChunks,
} from './hid-protocol';

export interface AgentInfo {
  buttons: number;
  physWidth: number;
  physHeight: number;
  fbWidth: number;
  fbHeight: number;
}

export type ButtonEventHandler = (button: number, pressed: boolean) => void;

export interface ButtonConfig {
  index: number;
  image?: ArrayBuffer;
  onPress?: () => void;
  onRelease?: () => void;
}

const INTER_CHUNK_DELAY_MS = 5;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class D200Agent {
  private device: HIDDevice;
  private onButton: ButtonEventHandler | null = null;
  private onError: ((msg: string) => void) | null = null;
  private inputReportHandler: ((event: HIDInputReportEvent) => void) | null =
    null;
  private buttonConfigs: Map<number, ButtonConfig> = new Map();
  private buttonDefs: ButtonDef[] = [];
  private currentBrightness = -1;
  private beforeUnloadHandler: (() => void) | null = null;

  constructor(device: HIDDevice) {
    this.device = device;
  }

  async start(
    onButton: ButtonEventHandler,
    onError: (msg: string) => void,
  ): Promise<AgentInfo> {
    this.onButton = onButton;
    this.onError = onError;

    const layout = getLayout();
    this.buttonDefs = layout.buttons;

    this.inputReportHandler = (event: HIDInputReportEvent) => {
      this.handleInputReport(event);
    };
    this.device.addEventListener('inputreport', this.inputReportHandler);

    this.beforeUnloadHandler = () => this.stop();
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    return {
      buttons: layout.buttons.length,
      physWidth: layout.physWidth,
      physHeight: layout.physHeight,
      fbWidth: layout.fbWidth,
      fbHeight: layout.fbHeight,
    };
  }

  private handleInputReport(event: HIDInputReportEvent): void {
    if (event.data.byteLength < 12) return;

    const header = new DataView(event.data.buffer, event.data.byteOffset, 4);
    const magic = header.getUint16(0, false);

    if (magic !== MAGIC) return;

    const command = header.getUint16(2, false);
    if (command !== CMD_IN_BUTTON) return;

    const payload = new DataView(
      event.data.buffer,
      event.data.byteOffset + 8,
      event.data.byteLength - 8,
    );
    const result = parseButtonEvent(payload);
    if (!result) return;

    this.onButton?.(result.button, result.pressed);
    this.dispatchButtonConfig(result.button, result.pressed);
  }

  private dispatchButtonConfig(button: number, pressed: boolean): void {
    const config = this.buttonConfigs.get(button);
    if (!config) return;
    if (pressed) {
      config.onPress?.();
    } else {
      config.onRelease?.();
    }
  }

  private async sendChunks(chunks: Uint8Array[]): Promise<void> {
    for (const chunk of chunks) {
      try {
        await this.device.sendReport(0, chunk.buffer as ArrayBuffer);
        if (chunks.length > 1) {
          await delay(INTER_CHUNK_DELAY_MS);
        }
      } catch (err) {
        this.onError?.(
          `HID send error: ${err instanceof Error ? err.message : String(err)}`,
        );
        throw err;
      }
    }
  }

  private buttonToEntry(buttonIndex: number, pngData: Uint8Array): ButtonEntry {
    const def = this.buttonDefs[buttonIndex];
    if (!def) throw new Error(`Invalid button index: ${buttonIndex}`);
    return {
      col: def.col,
      row: def.row,
      png: pngData,
    };
  }

  async drawButton(button: number, pngData: ArrayBuffer): Promise<void> {
    const entry = this.buttonToEntry(button, new Uint8Array(pngData));
    const zip = buildButtonZip([entry]);
    const chunks = splitIntoChunks(zip, CMD_PARTIAL_UPDATE);
    await this.sendChunks(chunks);
  }

  async drawButtonBase64(button: number, base64Png: string): Promise<void> {
    const binary = atob(base64Png);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    await this.drawButton(button, bytes.buffer);
  }

  async fillButton(
    button: number,
    r: number,
    g: number,
    b: number,
  ): Promise<void> {
    const def = this.buttonDefs[button];
    const canvas = new OffscreenCanvas(def.width, def.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fillRect(0, 0, def.width, def.height);
    const blob = await canvas.convertToBlob({ type: 'image/png' });
    const pngData = await blob.arrayBuffer();
    await this.drawButton(button, pngData);
  }

  async clear(): Promise<void> {
    const entries: ButtonEntry[] = [];
    const pngCache = new Map<string, Uint8Array>();

    for (const def of this.buttonDefs) {
      const sizeKey = `${def.width}x${def.height}`;
      let blackPng = pngCache.get(sizeKey);

      if (!blackPng) {
        const canvas = new OffscreenCanvas(def.width, def.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, def.width, def.height);
        const blob = await canvas.convertToBlob({ type: 'image/png' });
        blackPng = new Uint8Array(await blob.arrayBuffer());
        pngCache.set(sizeKey, blackPng);
      }

      entries.push({ col: def.col, row: def.row, png: blackPng });
    }

    const zip = buildButtonZip(entries);
    const chunks = splitIntoChunks(zip, CMD_SET_BUTTONS);
    await this.sendChunks(chunks);
  }

  async setBrightness(value: number): Promise<void> {
    this.currentBrightness = value;
    const packet = buildBrightnessPacket(value);
    try {
      await this.device.sendReport(0, packet.buffer as ArrayBuffer);
    } catch (err) {
      this.onError?.(
        `HID send error: ${err instanceof Error ? err.message : String(err)}`,
      );
      throw err;
    }
  }

  async getBrightness(): Promise<number> {
    return this.currentBrightness;
  }

  async ping(): Promise<void> {
    if (!this.device.opened) {
      throw new Error('HID device is not opened');
    }
  }

  async configureButton(
    index: number,
    config: Omit<ButtonConfig, 'index'>,
  ): Promise<void> {
    this.buttonConfigs.set(index, { index, ...config });
    if (config.image) {
      await this.drawButton(index, config.image);
    }
  }

  async configureButtons(configs: ButtonConfig[]): Promise<void> {
    for (const config of configs) {
      this.buttonConfigs.set(config.index, config);
    }

    const entries: ButtonEntry[] = [];
    for (const config of configs) {
      if (config.image) {
        entries.push(
          this.buttonToEntry(config.index, new Uint8Array(config.image)),
        );
      }
    }

    if (entries.length === 0) return;

    const command =
      entries.length === this.buttonDefs.length
        ? CMD_SET_BUTTONS
        : CMD_PARTIAL_UPDATE;

    const zip = buildButtonZip(entries);
    const chunks = splitIntoChunks(zip, command);
    await this.sendChunks(chunks);
  }

  stop(): void {
    if (this.inputReportHandler) {
      this.device.removeEventListener('inputreport', this.inputReportHandler);
      this.inputReportHandler = null;
    }
    this.buttonConfigs.clear();
    this.onButton = null;
    this.onError = null;
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }
}
